import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { DEFAULT_LOCALE } from 'constants/app-config'
import { scheduleAlternateLocalePrefetch } from 'i18n/utils/prefetchAlternateLocale'
import { localeFromPath } from 'i18n/utils/localeFromPath'
import { createRouteNamespaces } from 'i18n/utils/routeNamespaces'
import { createNamespaceLoader } from 'i18n/utils/namespaceLoader'

export const CORE_I18N_NAMESPACES = ['common', 'auth', 'form_errors', 'cookie_banner']

/** i18n for app shell (sidebar); loaded when `extraNamespaces` includes it for logged-in users */
export const AUTHORIZED_SHELL_I18N_NAMESPACES = ['navigation', 'onboarding']

/**
 * @param {object} options
 * @param {Record<string, Record<string, () => Promise<unknown>>>} options.namespaceLoaders
 * @param {Record<string, string[]>} options.domainToI18nNamespaces
 */
export const createProductI18n = ({ namespaceLoaders, domainToI18nNamespaces }) => {
  const routeNamespaces = createRouteNamespaces(CORE_I18N_NAMESPACES, domainToI18nNamespaces)
  const loadI18nNamespaces = createNamespaceLoader(i18n, namespaceLoaders)

  const shellNamespaces = () => [...new Set([...CORE_I18N_NAMESPACES, ...AUTHORIZED_SHELL_I18N_NAMESPACES])]

  /** Sidebar / shell: core + navigation — must load before MainLayout renders Nav */
  const ensureAuthorizedShellI18nLoaded = async ({ locale = i18n.language || localeFromPath() } = {}) => {
    const namespaces = shellNamespaces()
    await loadI18nNamespaces(locale, namespaces)
    scheduleAlternateLocalePrefetch(loadI18nNamespaces, { activeLocale: locale, namespaces })
  }

  const ensureI18nNamespacesLoaded = async ({
    routeKey,
    routePath,
    pathname,
    locale = i18n.language || localeFromPath(),
    extraNamespaces = [],
  } = {}) => {
    const namespaces = [...new Set([...routeNamespaces(routeKey, pathname), ...extraNamespaces])]
    await loadI18nNamespaces(locale, namespaces)
    scheduleAlternateLocalePrefetch(loadI18nNamespaces, { activeLocale: locale, namespaces })
  }

  i18n.use(initReactI18next).init({
    ns: [...CORE_I18N_NAMESPACES, ...AUTHORIZED_SHELL_I18N_NAMESPACES],
    defaultNS: 'common',
    fallbackLng: DEFAULT_LOCALE,
    lng: localeFromPath(),
    resources: {},
    returnEmptyString: true,
    pluralSeparator: '__',
    nsSeparator: '::',
    interpolation: {
      escapeValue: false,
    },
  })

  return {
    i18n,
    ensureAuthorizedShellI18nLoaded,
    ensureI18nNamespacesLoaded,
  }
}
